import type { BadgeColor } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  AppInput,
  AppSelectPopover,
  AppTextarea,
  AppTooltip,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { recruitmentTypeOptions, toRecruitmentTypeFormValue } from '@inquiries/-lib/recruitment-type-options'
import type { InquiryResponse } from 'backend/types'
import {
  INQUIRY_ASSIGN_STATUS_COLOR_MAP,
  INQUIRY_ASSIGN_STATUSES,
  INQUIRY_PRIORITY_COLOR_MAP,
  INQUIRY_PRIORITY_LABEL_MAP,
  type InquiryPriority,
  SALES_STAGE_MAP,
} from 'shared'
import { AppStartAsapCheckbox } from '@/components/AppStartAsapCheckbox'
import { useInquiryAssignStatuses } from '../../../hooks/useInquiryAssignStatuses'
import type { InquiryEditFormValues } from '../../../hooks/useInquiryEditForm'

type StatusScheduleSectionProps = {
  inquiry: InquiryResponse
  firstMonthSalesStageId: number | null
  formValues: InquiryEditFormValues
  formErrors: Partial<Record<keyof InquiryEditFormValues, string>>
  onFieldChange: <K extends keyof InquiryEditFormValues>(key: K, value: InquiryEditFormValues[K]) => void
  isInquiryCompleted: boolean
}

const priorityBadgeOptions: SelectOption[] = (
  Object.keys(INQUIRY_PRIORITY_LABEL_MAP) as (keyof typeof INQUIRY_PRIORITY_LABEL_MAP)[]
).map((key) => ({
  value: key,
  label: INQUIRY_PRIORITY_LABEL_MAP[key],
  color: INQUIRY_PRIORITY_COLOR_MAP[key] as BadgeColor,
}))

// TODO: GET /api/inquiry-assign-statuses のレスポンスに color フィールドが追加されたら
// INQUIRY_ASSIGN_STATUS_COLOR_MAP を廃止し、APIから取得した色を使用する
function getStatusColor(name: string): BadgeColor {
  return (INQUIRY_ASSIGN_STATUS_COLOR_MAP[name as keyof typeof INQUIRY_ASSIGN_STATUS_COLOR_MAP] ??
    'secondary') as BadgeColor
}

export function StatusScheduleSection({
  inquiry,
  firstMonthSalesStageId,
  formValues,
  formErrors,
  onFieldChange,
  isInquiryCompleted,
}: StatusScheduleSectionProps) {
  const { data: statusesData } = useInquiryAssignStatuses()
  const statuses = statusesData?.inquiry_assign_statuses ?? []

  const statusBadgeOptions: SelectOption[] = statuses
    .filter((s) => s.enabled)
    .map((s) => ({ value: String(s.id), label: s.name, color: getStatusColor(s.name) }))

  const handleStatusSelect = (value: string) => {
    const statusId = Number(value)
    const status = statuses.find((s) => s.id === statusId)
    if (!status) return

    onFieldChange('inquiryAssignStatusId', statusId)
    if (statusId !== INQUIRY_ASSIGN_STATUSES.WITHDRAWN) {
      onFieldChange('inquiryCloseReason', '')
    }
  }

  const ssStage = firstMonthSalesStageId ? SALES_STAGE_MAP[firstMonthSalesStageId] : undefined

  const isWithdrawn = formValues.inquiryAssignStatusId === INQUIRY_ASSIGN_STATUSES.WITHDRAWN

  return (
    <section className='space-y-3'>
      <h3 className='text-base font-semibold'>ステータス・スケジュール</h3>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div>
          <AppSelectPopover
            label='ステータス'
            currentValue={String(formValues.inquiryAssignStatusId)}
            options={statusBadgeOptions}
            onSelect={handleStatusSelect}
            header='ステータスを変更'
            contentClassName='w-40'
            triggerVariant='button'
            fallbackLabel={inquiry.inquiry_assign_status_name}
          />
          {formErrors.inquiryAssignStatusId && (
            <p className='mt-1 text-sm text-destructive'>{formErrors.inquiryAssignStatusId}</p>
          )}
        </div>
        <div>
          <AppSelectPopover
            label='募集形態'
            currentValue={formValues.recruitmentType}
            options={recruitmentTypeOptions}
            onSelect={(value) => onFieldChange('recruitmentType', toRecruitmentTypeFormValue(value))}
            header='募集形態を変更'
            contentClassName='w-32'
            triggerVariant='button'
            disabled={isInquiryCompleted}
          />
        </div>
        {isWithdrawn && (
          <div className='md:col-span-2'>
            <AppTextarea
              label='取り下げ理由'
              value={formValues.inquiryCloseReason}
              onChange={(e) => onFieldChange('inquiryCloseReason', e.target.value)}
              required
              rows={2}
              error={formErrors.inquiryCloseReason}
            />
          </div>
        )}
        <div className='md:col-start-1'>
          <AppSelectPopover
            label='優先度'
            currentValue={formValues.priority}
            options={priorityBadgeOptions}
            onSelect={(value) => onFieldChange('priority', value as InquiryPriority)}
            header='優先度を変更'
            contentClassName='w-32'
            triggerVariant='button'
            disabled={isInquiryCompleted}
          />
        </div>
        {ssStage ? (
          <AppTooltip content={ssStage.tooltip}>
            <div>
              <AppInput label='SS(ANOの初月)' value={ssStage.text} disabled />
            </div>
          </AppTooltip>
        ) : (
          <AppInput label='SS(ANOの初月)' value='-' disabled />
        )}
        <div className='space-y-2'>
          <AppInput
            label='開始日'
            type='date'
            value={formValues.isStartAsap ? '' : formValues.startOn}
            onChange={(e) => onFieldChange('startOn', e.target.value)}
            disabled={isInquiryCompleted || formValues.isStartAsap}
          />
          <AppStartAsapCheckbox
            checked={formValues.isStartAsap}
            onCheckedChange={(checked) => onFieldChange('isStartAsap', checked)}
            disabled={isInquiryCompleted}
          />
        </div>
        <AppInput
          label='終了日'
          type='date'
          value={formValues.endOn}
          onChange={(e) => onFieldChange('endOn', e.target.value)}
          error={formErrors.endOn}
          disabled={isInquiryCompleted}
        />
      </div>
    </section>
  )
}
